class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
        // MoBe : sur la page panier, le lien du header recharge la page (a jour) au lieu d'ouvrir un
    // tiroir que la page panier ne rafraichit pas (compte et montants figes au chargement).
    if (!cartLink || window.location.pathname === window.routes.cart_url) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

(() => {
  function initCartDrawerRecoArrows() {
    const root = document.getElementById("CartDrawer");
    if (!root) return;

    root.querySelectorAll(".cart-drawer__reco").forEach((wrap) => {
      const slider = wrap.querySelector("#Slider-CartDrawer-Reco");
      const prev = wrap.querySelector('button[name="previous"]');
      const next = wrap.querySelector('button[name="next"]');
      if (!slider || !prev || !next) return;

      // Bind once per wrapper (Dawn can replace DOM, so wrapper will be new -> rebind)
      if (wrap.__recoBound) {
        // but still refresh disabled state
        wrap.__recoUpdate && wrap.__recoUpdate();
        return;
      }
      wrap.__recoBound = true;

      const getStep = () => {
        const first = slider.querySelector(".slider__slide");
        if (!first) return slider.clientWidth || 300;
        const styles = getComputedStyle(slider);
        const gap = parseFloat(styles.gap || styles.columnGap || "0") || 0;
        return first.getBoundingClientRect().width + gap;
      };

      const update = () => {
        const max = slider.scrollWidth - slider.clientWidth - 1;
        if (max <= 0) {
          prev.disabled = true;
          next.disabled = true;
          return;
        }
        prev.disabled = slider.scrollLeft <= 1;
        next.disabled = slider.scrollLeft >= max;
      };

      wrap.__recoUpdate = update;

      prev.addEventListener("click", (e) => {
        e.preventDefault();
        slider.scrollBy({ left: -getStep(), behavior: "smooth" });
      });

      next.addEventListener("click", (e) => {
        e.preventDefault();
        slider.scrollBy({ left: getStep(), behavior: "smooth" });
      });

      slider.addEventListener("scroll", () => requestAnimationFrame(update), { passive: true });
      window.addEventListener("resize", update);

      // First compute (and again shortly after open because drawer animates in)
      update();
      setTimeout(update, 150);
    });
  }

  // Run now + whenever cart drawer content is swapped
  document.addEventListener("DOMContentLoaded", initCartDrawerRecoArrows);
  document.addEventListener("shopify:section:load", initCartDrawerRecoArrows);

  const cd = document.getElementById("CartDrawer");
  if (cd) new MutationObserver(initCartDrawerRecoArrows).observe(cd, { childList: true, subtree: true });

  // If you have a patched cart-drawer open already, add this call in it.
  customElements.whenDefined("cart-drawer").then(() => {
    const Cls = customElements.get("cart-drawer");
    if (!Cls?.prototype?.open) return;

    const originalOpen = Cls.prototype.open;
    if (originalOpen.__patchedForReco) return;

    function patchedOpen(...args) {
      const res = originalOpen.apply(this, args);
      initCartDrawerRecoArrows();
      setTimeout(initCartDrawerRecoArrows, 180);
      return res;
    }
    patchedOpen.__patchedForReco = true;
    Cls.prototype.open = patchedOpen;
  });
})();
